import esriConfig from '@arcgis/core/config.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SOURCE_GEOCOMM } from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersSources';
import { createVectorTileLayer } from '@rsos/maps/providers/esri/layers/createVectorTileLayer';
import { FLOOR_SELECTOR } from '../../AdditionalData/EllingtonCard/CustomComponents/constants';

export const GEOCOMM_INDOOR_MAPS = 'geocomm-indoor-maps';

/**
 * Loops through the `gisServices` data to find the lowest index that GeoComm
 * indoor maps can be set. Local GIS layers should be below indoor maps.
 * Layers with higher index in layers.items are visible/on top
 */
export const useMinAllowableIndex = () => {
  // layers with a higher index in layers.items are visible
  const minIndex = useSelector(state => state.gisServices.gisServices.length);
  return minIndex;
};

/**
 * Premium feature: This component handles loading all GeoComm vector tile
 * layers for each spatial object. Subsequent visibility of each layer is
 * controlled by GeoCommFloorSelector
 * @param {Object} map
 * @param {Object} map.mapProvider - 'google' or 'esri'
 * @param {Object} map.mapInstance
 */
const GeoCommIndoorMaps = React.memo(({ map }) => {
  const [visibleLayerIds, setVisibleLayerIds] = useState([]);
  const spatialLayer = useSelector(state => {
    // Use .includes() because nonprod has a suffix. Matches sprite icons handling
    return state.spatialLayers.staticLayers.find(layer => {
      return layer.source.toLowerCase().includes(SOURCE_GEOCOMM);
    });
  });

  const spatialLayerIsChecked = useMemo(
    () => spatialLayer?.isChecked,
    [spatialLayer?.isChecked],
  );

  const spatialObjects = useMemo(() => {
    return spatialLayer?.spatial_objects || [];
  }, [spatialLayer]);

  const indoorMapSpatialObjects = useMemo(() => {
    return spatialObjects.filter(obj => obj.attributes['floor-selector']);
  }, [spatialObjects]);

  const accessToken = useMemo(() => {
    return spatialLayer?.integration_access?.access_token || '';
  }, [spatialLayer]);

  const minAllowableIndex = useMinAllowableIndex();

  const getIndoorMapsLayers = () => {
    if (map?.mapInstance?.map?.layers?.items?.length) {
      return map.mapInstance.map.layers.items.filter(layer => {
        return layer.id.includes(GEOCOMM_INDOOR_MAPS);
      });
    }
    return [];
  };

  const getSpatialObject = useCallback(
    id => {
      const obj = indoorMapSpatialObjects.find(spatialObject => {
        return spatialObject.id === id;
      });
      return obj;
    },
    [indoorMapSpatialObjects],
  );

  const handleConfig = useCallback(() => {
    esriConfig.request.trustedServers.push('*');
    esriConfig.request.interceptors.push({
      urls: 'https://content.geocomm.cloud/indoor/v1/buildinglocations',
      before: params => {
        // use the headers passed in with params, but if header is null, then declare headers as empty.
        params.requestOptions.headers = params.requestOptions.headers || {};

        params.requestOptions.headers.Authorization = `Bearer ${accessToken}`;
      },
      after: response => {
        if (!response.ssl) {
          response.ssl = true;
        }
      },
      error: () => {
        // do nothing. prevent sentry from capturing these errors
      },
      proxyRules: {},
    });
  }, [accessToken]);

  /**
   * Load the vector tile URLs for each spatial object's default floor.
   * Other vector tiles will be rendered on the fly when interacting with
   * GeoCommFloorSelector
   */
  const handleInitialVectorTileLayers = () => {
    indoorMapSpatialObjects.forEach(spatialObject => {
      const floorSelectorData = spatialObject.attributes[FLOOR_SELECTOR];
      if (floorSelectorData?.floorDetails?.length) {
        floorSelectorData.floorDetails.forEach(async floor => {
          const isMainFloor =
            parseInt(floorSelectorData.building.floor) === floor.floorIndex;
          if (isMainFloor) {
            try {
              const vectorTileLayer = await createVectorTileLayer({
                id: `${GEOCOMM_INDOOR_MAPS}__${spatialObject.id}__${floor.vectorTileUrl}`,
                url: floor.vectorTileUrl,
                visible: spatialLayer?.isChecked || spatialLayer?.auto_display,
                title: floorSelectorData?.building?.siteName,
              });

              map.mapInstance.map.add(vectorTileLayer, minAllowableIndex);
            } catch (error) {
              // do nothing. suppress sentry
            }
          }
        });
      }
    });
  };

  useEffect(() => {
    if (indoorMapSpatialObjects.length) {
      handleInitialVectorTileLayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indoorMapSpatialObjects]);

  useEffect(() => {
    if (accessToken) {
      handleConfig();
    }
  }, [accessToken, handleConfig]);

  // Set state with a list of layer IDs for the currently visible vector tile
  // layers for toggling the LayerCheckbox on/off
  useEffect(() => {
    if (spatialLayer) {
      const indoorMapsLayers = getIndoorMapsLayers();
      if (spatialLayer.isChecked) {
        // If GeoComm isn't set to auto_display, `visibleLayerIds` will be an
        // empty list the first time the LayerCheckbox is toggled on.
        // Set the default floors to be visible
        if (!visibleLayerIds.length) {
          indoorMapsLayers.forEach(layer => {
            const spatialObjectId = parseInt(layer.id.split('__')[1]);
            const spatialObject = getSpatialObject(spatialObjectId);
            const floorSelectorData = spatialObject.attributes[FLOOR_SELECTOR];
            const defaultFloor = parseInt(floorSelectorData.building.floor);
            const shouldShowFloor = floorSelectorData?.floorDetails.find(
              floor =>
                floor.floorIndex === defaultFloor &&
                floor.vectorTileUrl === layer.url,
            );
            layer.visible = !!shouldShowFloor;
          });
        } else {
          indoorMapsLayers.forEach(layer => {
            if (visibleLayerIds.includes(layer.id)) {
              layer.visible = true;
            }
          });
        }
      } else {
        if (indoorMapsLayers.length) {
          let ids = [];
          indoorMapsLayers.forEach(layer => {
            if (layer.visible) {
              ids.push(layer.id);
            }
            // Set all layers visibility to false
            layer.visible = false;
          });
          setVisibleLayerIds(ids);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialLayerIsChecked]);

  return <></>;
});

export default GeoCommIndoorMaps;
